import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className = '', sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={`z-50 overflow-hidden rounded-lg border border-gray-200 bg-gray-900 px-3 py-2 text-sm font-medium text-white shadow-lg transition-opacity duration-150 max-w-xs whitespace-nowrap ${className}`}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** Compound Tooltip root (Radix/shadcn-style). Use with TooltipTrigger + TooltipContent. */
function TooltipCompound({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipRoot>) {
  return (
    <TooltipRoot delayDuration={200} {...props}>
      {children}
    </TooltipRoot>
  );
}
TooltipCompound.displayName = 'TooltipCompound';

// --- Simple API (single component; collision-aware via Radix) ---

export interface SimpleTooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  disabled?: boolean;
  sideOffset?: number;
}

/**
 * Simple tooltip wrapper: wraps trigger with Radix tooltip for collision-aware positioning
 * (e.g. when button is at viewport edge, tooltip flips to stay on screen).
 * Includes TooltipProvider internally so it works without a root provider.
 */
export function SimpleTooltip({
  content,
  children,
  side = 'top',
  delay = 200,
  disabled = false,
  sideOffset = 4,
}: SimpleTooltipProps) {
  if (disabled) {
    return <>{children}</>;
  }
  return (
    <TooltipProvider delayDuration={delay}>
      <TooltipRoot delayDuration={delay}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} sideOffset={sideOffset}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export {
  TooltipCompound,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
};

/** Simple API (collision-aware): <Tooltip content="..." side="bottom">...</Tooltip> */
export const Tooltip = SimpleTooltip;
export type TooltipProps = SimpleTooltipProps;
